<template>
  <div class="min-h-screen flex">
    <div class="flex-1 flex flex-col justify-center px-8 lg:px-24 bg-white">
      <div class="max-w-md w-full mx-auto text-center">
        <img src="@/assets/logo_red_2.png" class="mx-auto h-30 w-auto mb-2">
        <h2 class="text-4xl font-black text-gray-900 mb-2">Crea tu cuenta</h2>
        <p class="text-gray-500 mb-8">Empieza a chatear con tu match ideal hoy mismo.</p>
      </div>

      <div class="max-w-md w-full mx-auto">
        <form @submit.prevent="handleSignup" class="space-y-4">
          <div>
            <label class="block text-sm font-bold mb-2">Nombre completo</label>
            <input v-model="form.full_name" type="text" placeholder="Tu nombre" required
                   class="input-matchi">
          </div>

          <div>
            <label class="block text-sm font-bold mb-2">Correo electrónico</label>
            <input v-model="form.email" type="email" placeholder="email@ejemplo.com" required
                   class="input-matchi">
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-bold mb-2">Género</label>
              <select v-model="form.gender" required class="select-matchi">
                <option :value="null" disabled>Selecciona</option>
                <option v-for="opt in GENDER_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-bold mb-2">Me interesa</label>
              <select v-model="form.interested_in" required class="select-matchi">
                <option :value="null" disabled>Busco...</option>
                <option v-for="opt in GENDER_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-bold mb-2">Fecha de nacimiento</label>
            <input v-model="form.birth_date" type="date" required
                   class="input-matchi">
            <p v-if="errorAge" class="text-red-500 text-xs font-bold mt-2 ml-2">
              ⚠️ Debes ser mayor de 18 años para ingresar.
            </p>
          </div>

          <div>
            <label class="block text-sm font-bold mb-2">Contraseña</label>
            <div class="relative">
              <input v-model="form.password"
                     :type="showPassword ? 'text' : 'password'"
                     placeholder="••••••••" required
                     class="w-full pr-12 input-matchi">
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-500 transition-colors"
              >
                <span v-if="showPassword">🙈</span>
                <span v-else>👁️</span>
              </button>
            </div>
          </div>

          <div class="flex items-start gap-3 px-2 py-2">
            <input v-model="form.accepted_terms" type="checkbox" id="terms" required
                   class="mt-1 w-4 h-4 text-romance-red border-gray-300 rounded focus:ring-pink-500">
            <label for="terms" class="text-[11px] text-gray-400 leading-tight">
              Al registrarme, confirmo que soy <strong>mayor de 18 años</strong> y que he leído y acepto los
              <router-link to="/terms" class="text-romance-red font-bold underline">términos y condiciones</router-link>
              de uso de la plataforma.
            </label>
          </div>

          <button type="submit" :disabled="loading || !form.accepted_terms"
                  class="w-full bg-gradient-to-r from-romance-red to-blue-500 text-white font-bold py-4 rounded-2xl shadow-lg hover:opacity-90 transition disabled:opacity-50 disabled:grayscale">
            {{ loading ? 'Creando cuenta...' : 'Registrarse' }}
          </button>
        </form>

        <p class="mt-8 text-center text-sm text-gray-500">
          ¿Ya tienes cuenta? <router-link to="/signin" class="text-romance-red font-bold">Inicia sesión</router-link>
        </p>
      </div>
    </div>

    <div class="hidden lg:block lg:flex-1 bg-pink-100 relative overflow-hidden">
      <img src="@/assets/img/front-img-signup.png" class="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-60">
      <div class="absolute inset-0 flex items-center justify-center p-12 text-center">
        <h3 class="text-5xl font-black text-white drop-shadow-xl italic">"Encontré a alguien que realmente me escucha."</h3>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
import { GENDER_OPTIONS } from '@/constants/userOptions'

const router = useRouter()
const loading = ref(false)
const errorAge = ref(false)
const showPassword = ref(false)

const form = ref({
  full_name: '',
  email: '',
  password: '',
  birth_date: '',
  gender: null,
  interested_in: null,
  username: '',
  accepted_terms: false,
  role_id: 2
})

const calculateAge = (birthday) => {
  const ageDifMs = Date.now() - new Date(birthday).getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

const handleSignup = async () => {
  if (!form.value.accepted_terms) return;

  // Validación extra para los nuevos campos
  if (!form.value.gender || !form.value.interested_in) {
    Swal.fire({
      icon: 'warning',
      title: 'Datos incompletos',
      text: 'Por favor selecciona tu género y qué te interesa.',
      confirmButtonColor: '#ec4899',
      customClass: { popup: 'rounded-[2rem]' }
    });
    return;
  }

  errorAge.value = false;

  const age = calculateAge(form.value.birth_date);
  if (age < 18) {
    errorAge.value = true;
    Swal.fire({
      icon: 'error',
      title: 'Acceso Restringido',
      text: 'Debes ser mayor de 18 años para entrar al club.',
      confirmButtonColor: '#ec4899',
      customClass: { popup: 'rounded-[2rem]' }
    });
    return;
  }

  loading.value = true;
  Swal.fire({
    title: 'Creando tu acceso...',
    html: 'Estamos preparando tu perfil VIP',
    allowOutsideClick: false,
    didOpen: () => { Swal.showLoading() },
    customClass: { popup: 'rounded-[2rem]' }
  });

  try {
    form.value.username = form.value.email;
    const res = await api.post('/users', form.value);

    if (res.data.id) {
      Swal.fire({
        icon: 'success',
        title: '¡Bienvenido al Club!',
        text: 'Tu cuenta ha sido creada. Revisá tu correo para el enlace de bienvenida.',
        confirmButtonText: 'Iniciar Sesión',
        confirmButtonColor: '#ec4899',
        customClass: { popup: 'rounded-[2rem]' }
      }).then(() => {
        router.push('/signin');
      });
    }
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Hubo un problema',
      text: err.response?.data?.error || 'No pudimos procesar tu registro. Intentá de nuevo.',
      confirmButtonColor: '#ef4444',
      customClass: { popup: 'rounded-[2rem]' }
    });
  } finally {
    loading.value = false;
  }
}
</script>